/**
 * The skill editor's form and the SKILL.md it stands for (SA-054): read a
 * file back into the fields, and show the file the fields would write.
 */
export interface FormState {
  name: string;
  description: string;
  whenToUse: string;
  argumentHint: string;
  allowedTools: string;
  body: string;
}

export const emptyForm: FormState = { name: '', description: '', whenToUse: '', argumentHint: '', allowedTools: '', body: '' };

/** Reads the fields back from a SKILL.md the way the server writes them (simple `key: value` lines). */
export function formFromSkillMd(text: string): FormState {
  const match = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(text.replace(/\r\n/g, '\n').trimStart());
  if (!match) return { ...emptyForm, body: text };
  const fields = new Map<string, string>();
  for (const line of (match[1] as string).split('\n')) {
    const kv = /^([A-Za-z_-]+):\s*(.*)$/.exec(line);
    if (kv) fields.set((kv[1] as string).toLowerCase(), (kv[2] as string).replace(/^"(.*)"$/, '$1'));
  }
  return {
    name: fields.get('name') ?? '',
    description: fields.get('description') ?? '',
    whenToUse: fields.get('when_to_use') ?? fields.get('when-to-use') ?? '',
    argumentHint: fields.get('argument-hint') ?? fields.get('argumenthint') ?? '',
    allowedTools: (fields.get('allowed-tools') ?? '').split(',').map((s) => s.trim().replace(/^"(.*)"$/, '$1')).filter(Boolean).join('\n'),
    body: (match[2] as string).trim(),
  };
}

/** The SKILL.md the form would write (shown when switching to the raw tab). */
export function renderPreview(form: FormState): string {
  const quote = (value: string) => (/[:#"'{}[\],&*!|>%@`]/.test(value) ? JSON.stringify(value) : value);
  const lines = ['---', `name: ${form.name.trim()}`, `description: ${quote(form.description.trim())}`];
  if (form.whenToUse.trim()) lines.push(`when_to_use: ${quote(form.whenToUse.trim())}`);
  if (form.argumentHint.trim()) lines.push(`argument-hint: ${quote(form.argumentHint.trim())}`);
  const tools = form.allowedTools.split('\n').map((s) => s.trim()).filter(Boolean);
  if (tools.length > 0) lines.push(`allowed-tools: ${tools.map(quote).join(', ')}`);
  lines.push('---', '', form.body.trim(), '');
  return lines.join('\n');
}

